/**
 * ImportDialog - Dialog para importacao de dados Excel
 * Suporta 4 formatos: Padrao, Fintech, PORT, LEV
 */
import { useRef } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../ui/dialog";
import { Button } from "../../ui/button";
import { Badge } from "../../ui/badge";
import { FileSpreadsheet, Download, Upload, Building2 } from "lucide-react";

export function ImportDialog({
  open,
  onOpenChange,
  tabelaParaImportacao,
  onBaixarTemplate,
  onImportarPadrao,
  onImportarFintech,
  onImportarPort,
  onImportarLev
}) {
  const inputPadraoRef = useRef(null);
  const inputFintechRef = useRef(null);
  const inputPortRef = useRef(null);
  const inputLevRef = useRef(null);

  const handleFileChange = async (event, importFn) => {
    const file = event.target.files[0];
    if (!file) return;
    
    try {
      await importFn(file, tabelaParaImportacao?.tipo_operacao);
      onOpenChange(false);
    } catch (error) {
      // Erro ja tratado no hook
    }
    
    // Limpar input
    event.target.value = '';
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5 text-emerald-600" />
            Importar Dados da Tabela
          </DialogTitle>
          <DialogDescription>
            Importe dados de um arquivo Excel para a tabela de comissoes
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-6 py-4">
          {/* Info da tabela */}
          {tabelaParaImportacao && (
            <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
              <p className="text-sm font-medium text-emerald-800">Importando para:</p>
              <p className="text-sm text-emerald-700">{tabelaParaImportacao.nome || "Tabela"}</p>
              <div className="flex gap-2 mt-2">
                <Badge variant="outline" className="text-xs">{tabelaParaImportacao.tipo_operacao}</Badge>
                {tabelaParaImportacao.instituicao && (
                  <Badge variant="outline" className="text-xs bg-blue-50">{tabelaParaImportacao.instituicao}</Badge>
                )}
              </div>
            </div>
          )}
          
          {/* Quatro opcoes de importacao */}
          <div className="grid grid-cols-4 gap-3">
            
            {/* Opcao 1: Template Padrao StiloCred */}
            <div className="border rounded-lg p-3 space-y-2">
              <h4 className="font-semibold text-emerald-800 flex items-center gap-2 text-sm">
                <FileSpreadsheet className="h-4 w-4" />
                Template Padrao
              </h4>
              <p className="text-xs text-slate-500">
                Modelo Excel completo
              </p>
              <Button 
                variant="outline" 
                size="sm"
                className="gap-2 w-full text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                onClick={onBaixarTemplate}
                data-testid="btn-baixar-template"
              >
                <Download className="h-4 w-4" />
                Baixar Template
              </Button>
              <label className="flex items-center justify-center gap-2 p-3 border-2 border-dashed border-emerald-300 rounded-lg cursor-pointer hover:bg-emerald-50 transition-colors">
                <Upload className="h-4 w-4 text-emerald-600" />
                <span className="text-xs text-emerald-700 font-medium">
                  Importar Padrao
                </span>
                <input
                  ref={inputPadraoRef}
                  type="file"
                  accept=".xlsx,.xls"
                  className="hidden"
                  onChange={(e) => handleFileChange(e, onImportarPadrao)}
                  data-testid="input-importar-padrao"
                />
              </label>
            </div>
            
            {/* Opcao 2: Relatorio Fintech */}
            <div className="border-2 border-blue-300 rounded-lg p-3 space-y-2 bg-blue-50">
              <h4 className="font-semibold text-blue-800 flex items-center gap-2 text-sm">
                <Building2 className="h-4 w-4" />
                Rel. FINTECH
              </h4>
              <p className="text-xs text-slate-600">
                Layout Fintech
              </p>
              <div className="p-2 bg-white rounded border text-xs text-slate-600">
                <p className="font-medium mb-1">Mapeamento:</p>
                <ul className="space-y-0.5">
                  <li>Nome Tabela &rarr; Produto</li>
                  <li>Tipo &rarr; Tipo Comissao</li>
                  <li>Comissao &rarr; Emp %</li>
                  <li>Prazo Maximo &rarr; Prazo</li>
                </ul>
              </div>
              <label className="flex items-center justify-center gap-2 p-3 border-2 border-dashed border-blue-400 rounded-lg cursor-pointer hover:bg-blue-100 transition-colors bg-white">
                <Upload className="h-4 w-4 text-blue-600" />
                <span className="text-xs text-blue-700 font-medium">
                  Importar Fintech
                </span>
                <input
                  ref={inputFintechRef}
                  type="file"
                  accept=".xlsx,.xls"
                  className="hidden"
                  onChange={(e) => handleFileChange(e, onImportarFintech)}
                  data-testid="input-importar-fintech"
                />
              </label>
            </div>
            
            {/* Opcao 3: Relatorio PORT */}
            <div className="border-2 border-orange-300 rounded-lg p-3 space-y-2 bg-orange-50">
              <h4 className="font-semibold text-orange-800 flex items-center gap-2 text-sm">
                <Building2 className="h-4 w-4" />
                Rel. PORT
              </h4>
              <p className="text-xs text-slate-600">
                Layout PORT Promotora
              </p>
              <div className="p-2 bg-white rounded border text-xs text-slate-600">
                <p className="font-medium mb-1">Mapeamento:</p>
                <ul className="space-y-0.5">
                  <li>Tabela &rarr; Produto</li>
                  <li>Valor de/a &rarr; Vl.Ini/Fim</li>
                  <li>Prazo A &rarr; Prazo</li>
                  <li>Comissao &rarr; Emp %</li>
                  <li>Bonus &rarr; Bonus %</li>
                  <li>Diferido &rarr; Dif. %</li>
                </ul>
              </div>
              <label className="flex items-center justify-center gap-2 p-3 border-2 border-dashed border-orange-400 rounded-lg cursor-pointer hover:bg-orange-100 transition-colors bg-white">
                <Upload className="h-4 w-4 text-orange-600" />
                <span className="text-xs text-orange-700 font-medium">
                  Importar PORT
                </span>
                <input
                  ref={inputPortRef}
                  type="file"
                  accept=".xlsx,.xls"
                  className="hidden"
                  onChange={(e) => handleFileChange(e, onImportarPort)}
                  data-testid="input-importar-port"
                />
              </label>
            </div>

            {/* Opcao 4: Relatorio LEV */}
            <div className="border-2 border-purple-300 rounded-lg p-3 space-y-2 bg-purple-50">
              <h4 className="font-semibold text-purple-800 flex items-center gap-2 text-sm">
                <Building2 className="h-4 w-4" />
                Rel. LEV
              </h4>
              <p className="text-xs text-slate-600">
                Layout Levantamento
              </p>
              <div className="p-2 bg-white rounded border text-xs text-slate-600">
                <p className="font-medium mb-1">Mapeamento:</p>
                <ul className="space-y-0.5">
                  <li>Tabela &rarr; Produto</li>
                  <li>Valor de/a &rarr; Vl.Ini/Fim</li>
                  <li>Prazo &rarr; Prazo</li>
                  <li>Comissao &rarr; Emp %</li>
                  <li>Bonus &rarr; Bonus %</li>
                  <li>Diferido &rarr; Dif. %</li>
                </ul>
              </div>
              <label className="flex items-center justify-center gap-2 p-3 border-2 border-dashed border-purple-400 rounded-lg cursor-pointer hover:bg-purple-100 transition-colors bg-white">
                <Upload className="h-4 w-4 text-purple-600" />
                <span className="text-xs text-purple-700 font-medium">
                  Importar LEV
                </span>
                <input
                  ref={inputLevRef}
                  type="file"
                  accept=".xlsx,.xls"
                  className="hidden"
                  onChange={(e) => handleFileChange(e, onImportarLev)}
                  data-testid="input-importar-lev"
                />
              </label>
            </div>
            
          </div>
          
          <p className="text-xs text-slate-500 text-center">
            Formatos aceitos: .xlsx, .xls
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
